import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CheckCircle, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function changeIngressAccount(id: string) {
  return api.backend.put(`/application-account-users/change-ingress/${id}`)
}

export function useChangeIngressAccount() {
  const { t } = useTranslation('profile')
  const { handleModal, toggleLoading } = useModalProvider()

  const { mutateAsync } = useMutation<unknown, AxiosError<ApiError>, string>({
    mutationFn: changeIngressAccount,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CheckCircle,
          title: t('access.change_ingress.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['/user/current-accounts'],
      })
      queryClient.refetchQueries({
        queryKey: ['/user/get-user-info'],
      })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('access.change_ingress.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('access.change_ingress.toast_error.description'),
        },
      })
    },
  })

  const handleChangeIngressAccount = async (
    id: string,
    applicationName: string,
    accountName: string,
  ) => {
    handleModal({
      type: 'warning',
      title: t('access.change_ingress.modal.title'),
      description: t('access.change_ingress.modal.description', {
        applicationName,
        accountName,
      }),
      confirmText: t('access.change_ingress.modal.button_confirm'),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(id).finally(() => {
          toggleLoading()
        })
      },
    })
  }

  return {
    handleChangeIngressAccount,
  }
}
